/**
 * Wire-format types for the Activity API. Mirror
 * CSC_CORE.Features.Activity DTOs and enums.
 */

import type { ActivityKind } from '../lib/kindMap';

export enum ActivitySeverity {
  Info = 0,
  Notable = 1,
  Critical = 2,
}

/**
 * The nine render kinds (E5 contract C23, `CSC_CORE/Features/Activity/ActivityKinds.cs`).
 *
 * Derived in `../lib/kindMap.ts` from the `KIND_TOKENS` array — the same array `npm run
 * scan:tokens` checks against the server vocabulary — and re-exported here so wire-format
 * importers keep this path. The union was hand-written here until 86d43b6gk; nothing checked it
 * against the C# list, so a tenth server kind would have rendered as the muted `system` tile
 * with every check green.
 *
 * `null` on every row written before E5 — the column is nullable and always will be. Those rows
 * render as `system`: the honest neutral, never a guess with a colour on it.
 */
export type { ActivityKind };

/**
 * One ready-to-draw evidence block (E9 contract §C1–C3).
 *
 * 🔑 **The client switches on `shape` and inspects nothing else.** It never learns an event type, a
 * projection axis, or a payload key — the server resolves all of that and hands over finished strings. That
 * is the whole design: a producer can change its payload keys and this file does not move.
 *
 * Every value is pre-formatted server-side. Numbers are already formatted, units are already appended, and
 * the client does no arithmetic and applies no unit rule.
 */
export interface ActivityEvidence {
  shape: 'lines' | 'counts' | 'change' | 'transition' | 'note';

  /** shape = "lines" — column headers, positionally aligned to each row. */
  columns?: string[];
  /** shape = "lines" — a matrix of strings; a null cell renders as an em-dash. */
  rows?: (string | null)[][];
  /**
   * shape = "lines" — `'in'` / `'out'` when the block's quantities move stock; absent for the neutral
   * majority. Server-declared per projection entry (arrows mark POSTED movement only); an unrecognised
   * token renders neutral, never throws.
   */
  direction?: string | null;

  /** shape = "counts" — the compact strip, e.g. `2 items · 80 KG`. */
  counts?: { label: string; value: string }[];

  /** shape = "change" — one entry per changed field. */
  changes?: { field: string; from: string | null; to: string | null }[];

  /** shape = "transition" */
  from?: string | null;
  to?: string | null;
  tone?: string | null;

  /** shape = "note" — the reason tag's text. */
  text?: string | null;
}

export interface ActivityLogDto {
  id: number;
  occurredAtUtc: string;
  actorUserId: number | null;
  actorSnapshot: string;
  module: string;
  entityType: string;
  entityId: string;
  entityLabel: string;
  actionType: string;
  summary: string;
  detailsJson: string | null;
  correlationId: string | null;
  /** ⚠️ Always null on the per-entity feed — the server drops it there (F-1). Present on the admin feed. */
  ipAddress: string | null;
  /** @see ipAddress — same rule, and dropped system-wide going forward (owner ruling 2026-08-17). */
  userAgent: string | null;
  severity: ActivitySeverity;

  /** The DomainEvents row this was projected from. Null on every pre-E5 row, permanently. */
  sourceEventId: string | null;
  /** @see ActivityKind — null means pre-E5; render as `system`. */
  kind: ActivityKind | null;
  /** `http:web` | `http:mobile` | `worker:{ClassName}`. Null renders NO origin suffix, never a default. */
  source: string | null;
  /** Empty for most rows, and that is the expected case rather than a gap. */
  evidence: ActivityEvidence[];
  /**
   * True for milestone events (close / post / cancel / fulfil) — rows grouping must never fold into a
   * collapsed run. Server-declared on the projection entry and resolved at read time, exactly like
   * `evidence`; the client holds NO ActionType set of its own (the kindMap.ts registry rule).
   */
  milestone: boolean;
  /**
   * The feed tile's glyph token (`CSC_CORE/Features/Activity/ActivityIcons.cs`), for events that declare
   * one more specific than their kind's default. Server-declared on the projection entry and resolved at
   * read time, exactly like `milestone`; drawn via `ICON_GLYPHS` with a fallback to the kind glyph — the
   * client holds NO ActionType table of its own (the kindMap.ts registry rule). Null ⇒ kind glyph.
   */
  icon: string | null;
  /**
   * The tile's tint token (`CSC_CORE/Features/Activity/ActivityTones.cs`), for events whose OUTCOME earns
   * a colour (the completion milestones turn `success`). A tile property only — it never moves the row
   * between filter pills. Resolved at read time like `icon`. Null ⇒ the tile keeps its kind tint.
   */
  tileTone: string | null;
  /**
   * Typed facts a collapsed burst header aggregates from (86d43a7cq) — null unless the event declares an
   * `AggregationAxis` and every quantity parsed server-side. **The one deliberate exception to "the
   * client does no arithmetic":** grouping is a client presentation decision, so the client must also
   * sum — but only ever over these typed values, never by parsing display strings, and the whole rule
   * lives in `aggregateBurst`, a pure function vitest asserts. `verb` rides along so no ActionType maps
   * to a word anywhere in `src/`.
   */
  rollup: {
    verb: string;
    itemCodes: string[];
    quantities: { unit: string | null; value: number }[];
  } | null;
}

export interface GetActivityRequest {
  module?: string;
  actorUserId?: number;
  entityType?: string;
  entityId?: string;
  actionType?: string;
  fromUtc?: string;
  toUtc?: string;
  severity?: ActivitySeverity;
  /** Both halves travel together; one alone is a 400, not a reset to page 1. */
  cursorOccurredAtUtc?: string;
  cursorId?: number;
  pageSize?: number;
}

/**
 * Query for one record's feed. Note what is NOT here: the record itself. `entityType`/`entityId` come from
 * the route the service resolves, because the endpoint lives on the parent module's controller so the
 * parent's view rule authorizes it — a client-supplied entity id would walk straight around that check.
 */
export interface GetEntityActivityRequest {
  /** Both cursor halves travel together; one alone is a 400, not a reset to page 1. */
  cursorOccurredAtUtc?: string;
  cursorId?: number;
  pageSize?: number;
  /** The selected filter pill's kinds, already expanded from the group. Omit for "All". */
  kinds?: ActivityKind[];
}

/**
 * One keyset page of an activity feed — the record panel's and the admin page's alike.
 *
 * ⚠️ Named `ActivityFeedPage` rather than `ActivityPage` because that name already belongs to the admin
 * page COMPONENT, and two things called `ActivityPage` in one feature is a trap for whoever imports next.
 *
 * `totalCount` and `kindCounts` are **null on every request that carries a cursor** — they are aggregates
 * taken once, on the first page, and held by the client for the session (D-E). A "show more" that
 * recomputed them would run a `COUNT(*)` per scroll for a number nothing re-reads.
 */
export interface ActivityFeedPage {
  items: ActivityLogDto[];
  /** From a `pageSize + 1` probe, never from arithmetic against a count that may already be stale. */
  hasMore: boolean;
  nextCursorOccurredAtUtc: string | null;
  nextCursorId: number | null;
  /** The `M` in `Showing N of M`. On the admin feed it reflects the filter card; on the panel it does not
   *  reflect the kind pills, so M stays fixed while a pill narrows N. */
  totalCount: number | null;
  /** Per-kind counts feeding the panel's pills; rows with a null kind are counted under `system`.
   *  The admin feed has its own filter card and ignores this. */
  kindCounts: Record<string, number> | null;
}
